import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../utils/api';

export const fetchProducts = createAsyncThunk('products/fetchProducts', async (params, { rejectWithValue }) => {
  try {
    const { data } = await API.get('/products', { params });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
  }
});

export const fetchProductBySlug = createAsyncThunk('products/fetchBySlug', async (slug, { rejectWithValue }) => {
  try {
    const { data } = await API.get(`/products/slug/${slug}`);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Product not found');
  }
});

export const fetchRelatedProducts = createAsyncThunk('products/fetchRelated', async (productId, { rejectWithValue }) => {
  try {
    const { data } = await API.get(`/products/${productId}/related`);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const fetchProductReviews = createAsyncThunk('products/fetchReviews', async ({ productId, params }, { rejectWithValue }) => {
  try {
    const { data } = await API.get(`/reviews/product/${productId}`, { params });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const submitReview = createAsyncThunk('products/submitReview', async (reviewData, { rejectWithValue }) => {
  try {
    const { data } = await API.post('/reviews', reviewData);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to submit review');
  }
});

const productSlice = createSlice({
  name: 'products',
  initialState: {
    products: [],
    product: null,
    relatedProducts: [],
    reviews: [],
    pagination: null,
    reviewPagination: null,
    loading: false,
    productLoading: false,
    error: null,
    filters: {
      type: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      gender: '',
      material: '',
      sort: '-createdAt',
      search: '',
      page: 1,
    },
  },
  reducers: {
    setFilters: (state, { payload }) => {
      state.filters = { ...state.filters, ...payload };
    },
    clearFilters: (state) => {
      state.filters = {
        type: '', category: '', minPrice: '', maxPrice: '',
        gender: '', material: '', sort: '-createdAt', search: '', page: 1,
      };
    },
    clearProduct: (state) => {
      state.product = null;
      state.relatedProducts = [];
      state.reviews = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => { state.loading = true; })
      .addCase(fetchProducts.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.products = payload?.products || [];
        state.pagination = payload?.pagination || null;
      })
      .addCase(fetchProducts.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchProductBySlug.pending, (state) => { state.productLoading = true; })
      .addCase(fetchProductBySlug.fulfilled, (state, { payload }) => {
        state.productLoading = false;
        state.product = payload.product;
      })
      .addCase(fetchProductBySlug.rejected, (state, { payload }) => {
        state.productLoading = false;
        state.error = payload;
      })
      .addCase(fetchRelatedProducts.fulfilled, (state, { payload }) => {
        state.relatedProducts = payload?.products || [];
      })
      .addCase(fetchProductReviews.fulfilled, (state, { payload }) => {
        state.reviews = payload?.reviews || [];
        state.reviewPagination = payload?.pagination || null;
      })
      .addCase(submitReview.fulfilled, (state, { payload }) => {
        state.reviews.unshift(payload.review);
      });
  },
});

export const { setFilters, clearFilters, clearProduct } = productSlice.actions;
export default productSlice.reducer;
